// src/context/AuthContext.js — UNIFIED ROLE-BASED AUTH
// Handles 3 roles: VENDOR | MALL_ADMIN | SUPER_ADMIN
// Tokens are stored as httpOnly cookies set by the server — never in localStorage.

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

// In-memory token store — not in localStorage (security), not in state (no re-renders)
let _vendorToken = null;
export const getVendorToken = () => _vendorToken;

export function AuthProvider({ children, onVendorChange }) {
  const [role,    setRole]    = useState(null);
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const prevUserId = useRef(null);

  // Restore session on mount — cookies are sent automatically by the browser.
  // Try each role endpoint in priority order; the first that succeeds wins.
  useEffect(() => {
    // Clear any leftover localStorage tokens from the old implementation
    ['token','qs_vendor_token','qs_mall_token','qs_super_token',
     'qs_vendor_user','qs_mall_user','qs_super_user'].forEach(k => localStorage.removeItem(k));

    const restore = async () => {
      try {
        const r = await api.get('/auth/me');
        setUser(r.data); setRole('VENDOR');
        prevUserId.current = r.data?.id;
        setLoading(false); return;
      } catch (_) {}

      try {
        const r = await api.get('/mall/admin/me');
        setUser(r.data); setRole('MALL_ADMIN');
        setLoading(false); return;
      } catch (_) {}

      try {
        const r = await api.get('/super-admin/me');
        setUser(r.data); setRole('SUPER_ADMIN');
        setLoading(false); return;
      } catch (_) {}

      setLoading(false);
    };
    restore();
  }, []);

  // ── Vendor login ──────────────────────────────────────────
  const loginVendor = async (email, password) => {
    const res    = await api.post('/auth/login', { email, password });
    const vendor = res.data.vendor;
    _vendorToken = res.data.token || null;
    if (prevUserId.current && prevUserId.current !== vendor?.id) onVendorChange?.();
    prevUserId.current = vendor?.id;
    setUser(vendor); setRole('VENDOR');
    return vendor;
  };

  // ── Mall admin login ──────────────────────────────────────
  const loginMall = async (email, password) => {
    const res   = await api.post('/mall/login', { email, password });
    const admin = res.data.admin;
    setUser(admin); setRole('MALL_ADMIN');
    return admin;
  };

  // ── Super admin login ─────────────────────────────────────
  const loginSuperAdmin = async (email, password) => {
    const res   = await api.post('/super-admin/login', { email, password });
    const admin = res.data.admin;
    setUser(admin); setRole('SUPER_ADMIN');
    return admin;
  };

  // ── Vendor self-registration ─────────────────────────────
  const registerVendor = async (data) => {
    const res    = await api.post('/vendors/register', data);
    const vendor = res.data.vendor;
    _vendorToken = res.data.token || null;
    if (prevUserId.current && prevUserId.current !== vendor?.id) onVendorChange?.();
    prevUserId.current = vendor?.id;
    setUser(vendor); setRole('VENDOR');
    return vendor;
  };

  // ── Logout — tell the server to clear the httpOnly cookie ─
  const logout = async () => {
    try {
      if (role === 'VENDOR')      await api.post('/auth/logout').catch(() => {});
      if (role === 'MALL_ADMIN')  await api.post('/mall/logout').catch(() => {});
      if (role === 'SUPER_ADMIN') await api.post('/super-admin/logout').catch(() => {});
    } finally {
      _vendorToken = null;
      prevUserId.current = null;
      setUser(null); setRole(null);
      onVendorChange?.();
    }
  };

  // Patch the in-memory user (e.g. after PATCH /vendors/me or /mall/:mallId)
  // so changes show up immediately without requiring a re-login.
  const updateUser = (patch) => setUser((prev) => prev ? { ...prev, ...patch } : prev);

  // Convenience getters
  const isVendor     = role === 'VENDOR';
  const isMallAdmin  = role === 'MALL_ADMIN';
  const isSuperAdmin = role === 'SUPER_ADMIN';
  const vendor       = isVendor    ? user : null;
  const mallAdmin    = isMallAdmin ? user : null;
  const superAdmin   = isSuperAdmin? user : null;

  return (
    <AuthContext.Provider value={{
      role, user, loading, logout, updateUser,
      loginVendor, loginMall, loginSuperAdmin, registerVendor,
      isVendor, isMallAdmin, isSuperAdmin,
      vendor, mallAdmin, superAdmin,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
